"use client";

import { useContext } from "react";
import { useImmer } from "use-immer";
import { LockKeyhole } from "lucide-react";

import { PermissionContext } from "./context";
import { UserContext } from "@/contexts/user-context";
import { ResourceContext } from "@/contexts/resource-context";
import { getPermissionComponent } from "./permission-components";
import { useSyncUserNewsletter } from "./hooks/use-sync-user-newsletter";
import { ActionName, PermissionProps } from "./types";

function extractActionFlags(action: ResourceAccessAction | undefined) {
  if (!action?.data) {
    return { isStudent: false, isNurseOrMidwife: false };
  }

  const relevantActions = ["doctor_or_dentist", "drug_ad_recipient"];
  const isRelevantAction = relevantActions.includes(action.name);

  return {
    isStudent: isRelevantAction && Boolean(action.data.isStudent),
    isNurseOrMidwife:
      action.name === "drug_ad_recipient" &&
      Boolean(action.data.isNurseOrMidwife),
  };
}

// OrPermission przy kilku odmowach zwraca jedną akcję „OR” z alternatywami w `data` — pokazujemy pierwszą obsługiwaną
function unwrapOrAction(
  action: ResourceAccessAction | undefined,
): ResourceAccessAction | undefined {
  if (action?.name !== "OR" || !action.data) {
    return action;
  }

  // `data` to obiekt { nazwa_akcji: akcja } (Actions indeksuje po nazwie)
  return Object.values(action.data as Record<string, ResourceAccessAction>)
    .map(unwrapOrAction)
    .find(
      (alternative) =>
        alternative &&
        getPermissionComponent(
          alternative.name as ActionName,
          extractActionFlags(alternative),
        ),
    );
}

export function Permission({
  actions: actionsProp,
  redirectTo,
  resourceType,
}: PermissionProps) {
  const user = useContext(UserContext);
  const { access } = useContext(ResourceContext);

  const actions = actionsProp ?? access?.actions ?? {};
  const [action, setAction] = useImmer(unwrapOrAction(actions[0]));

  useSyncUserNewsletter(user, action?.name);

  if (!action) {
    return null;
  }

  const actionName = action.name as ActionName;
  const flags = extractActionFlags(action);
  const PermissionComponent = getPermissionComponent(actionName, flags);

  if (!PermissionComponent) {
    return null;
  }

  const isNewsletterRequired = actions[1]?.name === "newsletter";

  return (
    <PermissionContext.Provider value={{ resourceType }}>
      <div className="mx-auto mt-7 mb-[200px] max-w-[328px] md:mt-14">
        <div className="mx-auto mb-6 flex h-10 w-10 items-center justify-center rounded-md bg-teal-50 text-teal-600">
          <LockKeyhole size={16} />
        </div>
        <PermissionComponent
          isNewsletterRequired={isNewsletterRequired}
          access={action}
          setAccess={setAction}
          resourceType={resourceType}
          redirectTo={redirectTo}
        />
      </div>
    </PermissionContext.Provider>
  );
}
